import type { AiResolutionSuggestion, SupportTicket } from "@/lib/support";
import {
  STALE_DAYS,
  TODO_LABEL,
  TODO_RANK,
  URGENCY_RANK,
} from "@/pages/support/constants";

export function normalizeTodoState(rawState: string | null | undefined): string {
  const value = String(rawState || "").trim().toLowerCase();
  if (!value) return "eingang";
  const legacyMap: Record<string, string> = {
    backlog: "eingang",
    selected: "in_umsetzung",
    blocked: "wichtig",
    done: "erledigt",
  };
  if (legacyMap[value]) return legacyMap[value];
  if (TODO_LABEL[value]) return value;
  return "eingang";
}

export function fmtDate(iso: string | null | undefined): string {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("de-DE", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(iso);
  }
}

export function fmtRelative(iso: string | null | undefined): string {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return String(iso);
  const diffMs = Date.now() - t;
  const minutes = Math.floor(diffMs / 60000);
  if (minutes < 1) return "gerade eben";
  if (minutes < 60) return `vor ${minutes} Min.`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `vor ${hours} Std.`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `vor ${days} Tag${days === 1 ? "" : "en"}`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `vor ${weeks} Woche${weeks === 1 ? "" : "n"}`;
  const months = Math.floor(days / 30);
  if (months < 12) return `vor ${months} Monat${months === 1 ? "" : "en"}`;
  const years = Math.floor(days / 365);
  return `vor ${years} Jahr${years === 1 ? "" : "en"}`;
}

export function ageInDays(iso: string | null | undefined): number {
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return 0;
  return Math.floor((Date.now() - t) / 86400000);
}

/** Erstellungszeit für Sortierung; fällt auf Ticket-ID zurück wenn kein Datum geliefert wird. */
export function ticketCreatedTime(ticket: SupportTicket): number {
  const parsed = new Date(ticket.created_at || "").getTime();
  if (Number.isFinite(parsed) && parsed > 0) return parsed;
  return Number(ticket.id) || 0;
}

/** Stabiler Schlüssel der Ticket-Aktivität, an dem „ungelesen“ hängt. */
export function ticketReadActivityKey(
  ticket: Pick<SupportTicket, "last_reply_at" | "updated_at" | "created_at">,
): string {
  return [ticket.last_reply_at || "", ticket.updated_at || "", ticket.created_at || ""].join("|");
}

/**
 * Eine Liste vom Server darf ein gerade gelesenes Ticket nicht wieder als
 * ungelesen zeichnen, solange sich Antwort/Update nicht geändert haben.
 */
export function preserveLocallyReadTickets(
  incoming: SupportTicket[],
  readActivityById: ReadonlyMap<number, string>,
): { tickets: SupportTicket[]; readActivityById: Map<number, string> } {
  const nextReads = new Map(readActivityById);
  const tickets = incoming.map((ticket) => {
    const seen = nextReads.get(ticket.id);
    if (!ticket.is_unread) {
      nextReads.delete(ticket.id);
      return ticket;
    }
    if (seen != null && seen === ticketReadActivityKey(ticket)) {
      return { ...ticket, is_unread: false };
    }
    nextReads.delete(ticket.id);
    return ticket;
  });
  return { tickets, readActivityById: nextReads };
}

/** Letzte Aktivität (Antwort oder Update) für Melder-Sortierung. */
export function ticketActivityTime(ticket: SupportTicket): number {
  const raw = ticket.last_reply_at || ticket.updated_at || ticket.created_at || "";
  const parsed = new Date(raw).getTime();
  if (Number.isFinite(parsed) && parsed > 0) return parsed;
  return ticketCreatedTime(ticket);
}

export function ticketHasOutboundReply(ticket: SupportTicket): boolean {
  return Boolean(ticket.has_outbound_reply);
}

/** Roter Punkt: auf die Support-Antwort wurde ungelesen zurückgeschrieben. */
export function ticketHasUnreadIncomingReply(ticket: SupportTicket): boolean {
  if (!ticket.has_outbound_reply || !ticket.is_unread) return false;
  const last = String(ticket.last_reply_author_login || "").trim().toLowerCase();
  const reporter = String(ticket.reporter_login || "").trim().toLowerCase();
  return Boolean(last && reporter && last === reporter);
}

export function ticketNeedsReporterAction(ticket: SupportTicket): boolean {
  return Boolean(ticket.action_required);
}

/** Tickets mit neuer Support-Antwort zuerst, danach neueste Aktivität. */
export function sortUserTickets(items: SupportTicket[]): SupportTicket[] {
  return items.slice().sort((a, b) => {
    const action = Number(Boolean(b.action_required)) - Number(Boolean(a.action_required));
    if (action !== 0) return action;
    return ticketActivityTime(b) - ticketActivityTime(a) || (Number(b.id) || 0) - (Number(a.id) || 0);
  });
}

function urgencyRank(urgency: string | null | undefined): number {
  return URGENCY_RANK[String(urgency || "normal").toLowerCase()] ?? 9;
}

export function toastOk(msg: string) {
  const fn = (window as { toast?: { success?: (m: string) => void } }).toast?.success;
  if (fn) fn(`✔️ ${msg}`);
  else console.log(msg);
}

export function toastErr(msg: string) {
  const fn = (window as { toast?: { error?: (m: string) => void } }).toast?.error;
  if (fn) fn(`❗ ${msg}`);
  else console.error(msg);
}

export interface AdminFilters {
  urgency: string;
  status: string;
  program: string;
  reporter: string;
  todoState: string;
  todoOwner: string;
  tagId: string;
  quickView: string;
  statFilter: string;
  /** Nur ungelesene Tickets */
  unreadOnly: boolean;
  search: string;
  sort: "smart" | "newest" | "oldest" | "urgency";
}

export function isoToDatetimeLocal(iso: string | null | undefined): string {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    if (!Number.isFinite(d.getTime())) return "";
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return "";
  }
}

export function datetimeLocalToIso(local: string): string | null {
  const trimmed = local.trim();
  if (!trimmed) return null;
  const d = new Date(trimmed);
  if (!Number.isFinite(d.getTime())) return null;
  return d.toISOString();
}

export function applyAdminFilters(
  items: SupportTicket[],
  filters: AdminFilters,
  currentLogin: string,
  aiSuggestions: AiResolutionSuggestion[],
): SupportTicket[] {
  const search = filters.search.trim().toLowerCase();
  const aiResolvedIds = new Set(aiSuggestions.map((s) => Number(s.ticket_id)));

  return items.filter((ticket) => {
    const urgency = String(ticket.urgency || "").trim().toLowerCase();
    const status = String(ticket.status || "").trim().toLowerCase();
    const program = String(ticket.program || "").trim();
    const reporterLogin = String(ticket.reporter_login || "").trim().toLowerCase();
    const reporterDisplay = String(ticket.reporter_display || "").trim().toLowerCase();
    const todoState = normalizeTodoState(ticket.todo_state);
    const todoOwner = String(ticket.todo_owner_login || "").trim().toLowerCase();
    const ticketTagIds = Array.isArray(ticket.tags)
      ? ticket.tags.map((tag) => String(tag.id).trim()).filter(Boolean)
      : [];
    const tagNames = Array.isArray(ticket.tags)
      ? ticket.tags.map((tag) => String(tag.name || "").trim().toLowerCase()).join(" ")
      : "";
    const title = String(ticket.title || "").toLowerCase();

    if (filters.urgency && urgency !== filters.urgency) return false;
    if (filters.status && status !== filters.status) return false;
    if (filters.program && program !== filters.program) return false;
    if (filters.reporter && reporterLogin !== filters.reporter) return false;
    if (filters.todoState && todoState !== filters.todoState) return false;
    if (filters.todoOwner === "me" && todoOwner !== currentLogin) return false;
    if (filters.todoOwner === "unassigned" && todoOwner) return false;
    if (
      filters.todoOwner &&
      !["me", "unassigned", ""].includes(filters.todoOwner) &&
      todoOwner !== filters.todoOwner
    ) {
      return false;
    }
    if (filters.tagId && !ticketTagIds.includes(filters.tagId)) return false;
    if (filters.quickView !== "all" && todoState !== filters.quickView) return false;

    if (filters.statFilter === "critical" && urgency !== "critical") return false;
    if (filters.statFilter === "mine" && todoOwner !== currentLogin) return false;
    if (filters.statFilter === "unassigned" && todoOwner) return false;
    if (filters.statFilter === "stale") {
      const daysOld = ageInDays(ticket.updated_at || ticket.created_at);
      if (daysOld < STALE_DAYS || status === "closed" || status === "resolved") return false;
    }
    if (filters.statFilter === "ai_resolved" && !aiResolvedIds.has(Number(ticket.id))) {
      return false;
    }
    if (filters.unreadOnly && !ticket.is_unread) return false;

    if (search) {
      const haystack = `${ticket.id} ${title} ${program.toLowerCase()} ${reporterLogin} ${reporterDisplay} ${tagNames} ${String(ticket.my_note || "")}`;
      if (!haystack.toLowerCase().includes(search)) return false;
    }
    return true;
  });
}

/** Sortierung innerhalb einer Kanban-Spalte: Dringlichkeit, dann neueste */
export function sortBoardColumnTickets(items: SupportTicket[]): SupportTicket[] {
  return items.slice().sort((a, b) => {
    const urg = urgencyRank(a.urgency) - urgencyRank(b.urgency);
    if (urg !== 0) return urg;
    return ticketCreatedTime(b) - ticketCreatedTime(a) || (Number(b.id) || 0) - (Number(a.id) || 0);
  });
}

export function sortAdminTickets(items: SupportTicket[], sort: AdminFilters["sort"]): SupportTicket[] {
  const list = items.slice();
  const byNewest = (a: SupportTicket, b: SupportTicket) =>
    ticketCreatedTime(b) - ticketCreatedTime(a) || (Number(b.id) || 0) - (Number(a.id) || 0);
  const byOldest = (a: SupportTicket, b: SupportTicket) =>
    ticketCreatedTime(a) - ticketCreatedTime(b) || (Number(a.id) || 0) - (Number(b.id) || 0);

  if (sort === "newest") {
    list.sort(byNewest);
  } else if (sort === "oldest") {
    list.sort(byOldest);
  } else if (sort === "urgency") {
    list.sort(
      (a, b) => urgencyRank(a.urgency) - urgencyRank(b.urgency) || byNewest(a, b),
    );
  } else {
    list.sort((a, b) => {
      const urg = urgencyRank(a.urgency) - urgencyRank(b.urgency);
      if (urg !== 0) return urg;
      const todo =
        (TODO_RANK[normalizeTodoState(a.todo_state)] ?? 9) -
        (TODO_RANK[normalizeTodoState(b.todo_state)] ?? 9);
      if (todo !== 0) return todo;
      return byNewest(a, b);
    });
  }
  return list;
}

export function computeAdminStats(
  tickets: SupportTicket[],
  currentLogin: string,
  aiSuggestions: AiResolutionSuggestion[],
) {
  const stats = { all: 0, critical: 0, mine: 0, unassigned: 0, stale: 0, ai_resolved: 0 };
  const aiResolvedIds = new Set(aiSuggestions.map((s) => Number(s.ticket_id)));
  const isActive = (status: string) => status !== "closed" && status !== "resolved";
  for (const t of tickets) {
    const status = String(t.status || "").toLowerCase();
    if (isActive(status)) stats.all += 1;
    if (String(t.urgency || "").toLowerCase() === "critical" && isActive(status)) stats.critical += 1;
    const owner = String(t.todo_owner_login || "").toLowerCase();
    if (owner === currentLogin && isActive(status)) stats.mine += 1;
    if (!owner && isActive(status)) stats.unassigned += 1;
    const days = ageInDays(t.updated_at || t.created_at);
    if (days >= STALE_DAYS && isActive(status)) stats.stale += 1;
    if (aiResolvedIds.has(Number(t.id))) stats.ai_resolved += 1;
  }
  return stats;
}

export function buildReporterOptions(tickets: SupportTicket[]) {
  const map = new Map<string, string>();
  for (const t of tickets) {
    const login = String(t.reporter_login || "").trim().toLowerCase();
    if (!login) continue;
    const display = String(t.reporter_display || "").trim();
    map.set(login, display ? `${display} (${login})` : login);
  }
  return [...map.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label, "de"));
}

export function buildProgramOptions(tickets: SupportTicket[]) {
  return Array.from(
    new Set(tickets.map((t) => String(t.program || "").trim()).filter(Boolean)),
  )
    .sort((a, b) => a.localeCompare(b, "de", { sensitivity: "base" }))
    .map((p) => ({ value: p, label: p }));
}

export function buildOwnerOptions(tickets: SupportTicket[]) {
  return Array.from(
    new Set(tickets.map((t) => String(t.todo_owner_login || "").trim().toLowerCase()).filter(Boolean)),
  )
    .sort((a, b) => a.localeCompare(b, "de"))
    .map((login) => ({ value: login, label: login }));
}

export interface UserFilters {
  search: string;
  statusFilter: string;
}

export function computeUserStats(tickets: SupportTicket[]) {
  const stats = { all: tickets.length, active: 0, open: 0, in_progress: 0, waiting: 0, resolved: 0, closed: 0 };
  for (const t of tickets) {
    const status = String(t.status || "open").toLowerCase();
    if (status !== "closed" && status !== "resolved") stats.active += 1;
    if (status === "waiting") {
      if (t.action_required !== false) stats.waiting += 1;
    } else if (status in stats) {
      stats[status as keyof typeof stats] += 1;
    }
  }
  return stats;
}

export function applyUserFilters(items: SupportTicket[], filters: UserFilters): SupportTicket[] {
  const search = filters.search.trim().toLowerCase();
  return items.filter((ticket) => {
    const status = String(ticket.status || "open").toLowerCase();
    const title = String(ticket.title || "").toLowerCase();
    const program = String(ticket.program || "").toLowerCase();
    const id = String(ticket.id);

    if (filters.statusFilter === "active" && (status === "closed" || status === "resolved")) return false;
    if (filters.statusFilter === "waiting" && ticket.action_required === false) return false;
    if (filters.statusFilter !== "all" && filters.statusFilter !== "active" && status !== filters.statusFilter) {
      return false;
    }
    if (search) {
      const haystack = `${id} ${title} ${program}`;
      if (!haystack.includes(search)) return false;
    }
    return true;
  });
}
